import { useState, useMemo } from "react"
import { FileText, MapPin } from "lucide-react"
import type { OrderDocument } from "../data/types"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { getIntlLocale } from "@/lib/i18n/format"
import FinishAlert from "@/components/maps/FinishAlert"
import type { RouteAlert } from "@/reducers/overlaysSlice"

interface ZleceniaTabProps {
  orders: OrderDocument[]
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat(getIntlLocale("pl-PL")).format(value)
}

const formatTime = (hours: number) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (m === 0) return `${h} h`
  return `${h} h ${m} min`
}

const formatTimeDiff = (hours: number) => {
  const h = Math.floor(Math.abs(hours))
  const m = Math.round((Math.abs(hours) - h) * 60)
  const sign = hours >= 0 ? "+" : "-"
  if (h === 0) return `${sign}0 h ${m} min`
  if (m === 0) return `${sign}${h} h`
  return `${sign}${h} h ${m} min`
}

export function ZleceniaTab({ orders }: ZleceniaTabProps) {
  const { t } = useTranslation()
  const [isMapDisplayed, setIsMapDisplayed] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<OrderDocument | null>(null)

  const finishAlertData = useMemo(() => {
    if (!selectedOrder) return undefined
    return {
      visible: true,
      alertId: "report-adhoc",
      driverJid: selectedOrder.routeData.driverJid,
      updatedAt: selectedOrder.routeData.endTimestamp,
      type: "routeFinished" as const,
      details: {
        alertId: "report-adhoc",
        expectedDistance: selectedOrder.routeData.expectedDistance,
        actualDistance: selectedOrder.routeData.actualDistance,
        firstStopId: selectedOrder.routeData.firstStopId,
        lastStopId: selectedOrder.routeData.lastStopId,
        routeTimestamp: selectedOrder.routeData.routeTimestamp,
        startTimestamp: selectedOrder.routeData.startTimestamp,
        endTimestamp: selectedOrder.routeData.endTimestamp,
      },
    } as RouteAlert
  }, [selectedOrder])

  const handleShowMap = (order: OrderDocument) => {
    setSelectedOrder(order)
    setIsMapDisplayed(true)
  }

  return (
    <>
      {isMapDisplayed && (
        <FinishAlert
          onClose={() => setIsMapDisplayed(false)}
          providedFinishedRouteAlert={finishAlertData}
        />
      )}
      <div className="rounded-lg border border-[#e5e5e5] bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#e5e5e5] text-left">
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.cmrDocument")}
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.orderDate")}
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.revenue")}
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.distance")}
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.roadCosts")}
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.fuel")}
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.drivingTime")}
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.loadedEmpty")}
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[#737373]">
                {t("planner.stats.metrics.route")}
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              // Calculate loaded/empty km from distance and percentages
              const loadedKm = Math.round(
                (order.distance * order.loadedPercent) / 100,
              )
              const emptyKm = Math.round(
                (order.distance * order.emptyPercent) / 100,
              )

              return (
                <tr
                  key={order.id}
                  className="border-b border-[#e5e5e5] last:border-0"
                >
                  {/* Dokument CMR */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#737373]" />
                      <div>
                        <div className="cursor-pointer text-sm font-medium text-[#2563eb] hover:underline">
                          {order.documentName}
                        </div>
                        <div className="text-xs text-[#737373]">
                          {order.driverName}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* Data zlecenia */}
                  <td className="px-4 py-3 text-sm text-[#0a0a0a]">
                    {order.orderDate}
                  </td>
                  {/* Przychod */}
                  <td className="px-4 py-3 text-sm text-[#0a0a0a]">
                    {formatNumber(order.revenue)} €
                  </td>
                  {/* Dystans */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#0a0a0a]">
                      {formatNumber(order.distance)} km
                    </div>
                    <div
                      className={cn(
                        "text-xs font-medium",
                        order.distanceDiff > 0
                          ? "text-[#dc2626]"
                          : "text-[#15803d]",
                      )}
                    >
                      {order.distanceDiff > 0 ? "+" : ""}
                      {order.distanceDiff} km
                    </div>
                  </td>
                  {/* Oplaty drogowe */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#0a0a0a]">
                      {formatNumber(order.roadCosts)} €
                    </div>
                    <div
                      className={cn(
                        "text-xs font-medium",
                        order.roadCostsDiff > 0
                          ? "text-[#dc2626]"
                          : "text-[#15803d]",
                      )}
                    >
                      {order.roadCostsDiff > 0 ? "+" : ""}
                      {order.roadCostsDiff} €
                    </div>
                  </td>
                  {/* Paliwo */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#0a0a0a]">
                      {order.fuelPer100km} l
                    </div>
                    <div
                      className={cn(
                        "text-xs font-medium",
                        order.fuelDiff > 0
                          ? "text-[#dc2626]"
                          : "text-[#15803d]",
                      )}
                    >
                      {order.fuelDiff > 0 ? "+" : ""}
                      {order.fuelDiff} l
                    </div>
                  </td>
                  {/* Czas jazdy */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#0a0a0a]">
                      {formatTime(order.drivingTime)}
                    </div>
                    <div
                      className={cn(
                        "text-xs font-medium",
                        order.drivingTimeDiff > 0
                          ? "text-[#dc2626]"
                          : "text-[#15803d]",
                      )}
                    >
                      {formatTimeDiff(order.drivingTimeDiff)}
                    </div>
                  </td>
                  {/* Ladowne / Nieladowne */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-[#0a0a0a]">
                      {formatNumber(loadedKm)} km / {formatNumber(emptyKm)} km
                    </div>
                    <div className="text-xs text-[#737373]">
                      {order.loadedPercent}% / {order.emptyPercent}%
                    </div>
                  </td>
                  {/* Route */}
                  <td className="px-4 py-3">
                    <button
                      className="rounded border border-[#e5e5e5] p-2 hover:bg-[#f5f5f5]"
                      onClick={() => handleShowMap(order)}
                    >
                      <MapPin className="h-4 w-4 text-[#737373]" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
